import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { supabase } from "@/lib/supabase";
import {
  useSEOMeta,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generatePersonSchema,
} from "@/components/SEO";

interface StaffMember {
  id: string;
  name: string;
  role: string;
  department: string;
  image_url: string | null;
  join_date: string;
  status: string;
  email: string;
}

const departments = [
  {
    name: "Health",
    icon: "ri-heart-pulse-line",
    color: "bg-emerald-50 text-emerald-700 border-emerald-200",
    iconColor: "text-emerald-600",
    description: "Medical professionals ensuring quality healthcare delivery",
  },
  {
    name: "Education",
    icon: "ri-book-open-line",
    color: "bg-sky-50 text-sky-700 border-sky-200",
    iconColor: "text-sky-600",
    description: "Educators and focal points driving learning outcomes",
  },
  {
    name: "Operations",
    icon: "ri-settings-3-line",
    color: "bg-amber-50 text-amber-700 border-amber-200",
    iconColor: "text-amber-600",
    description: "Operations and project management specialists",
  },
  {
    name: "Protection",
    icon: "ri-shield-check-line",
    color: "bg-rose-50 text-rose-700 border-rose-200",
    iconColor: "text-rose-600",
    description: "GBV and protection experts safeguarding vulnerable groups",
  },
];

export default function OurTeamPage() {
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useSEOMeta({
    title: "Our Team - BHODEV Cameroon | Leadership & Staff Members",
    description:
      "Meet the dedicated team at BHODEV Cameroon. Our multidisciplinary team of medical doctors, nurses, educators, and social entrepreneurs drives health, education, and protection programs across Cameroon.",
    keywords:
      "BHODEV team, BHODEV staff, Cameroon NGO team, Hans Asa Tanga, BHODEV leadership, NGO professionals Cameroon, community health workers, humanitarian team Cameroon",
    canonicalUrl: "https://bhodevcameroon.org/our-team",
    ogImage:
      "https://readdy.ai/api/search-image?query=diverse%20African%20healthcare%20development%20team%20professionals%20gathered%20together%20group%20photo%20community%20organization%20Cameroon%20NGO%20workers%20smiling%20together%20vibrant%20hopeful&width=1200&height=630&seq=team-og&orientation=landscape",
    schemaData: {
      ...generateBreadcrumbSchema([
        { name: "Home", url: "https://bhodevcameroon.org/" },
        { name: "Our Team", url: "https://bhodevcameroon.org/our-team" },
      ]),
      ...generateWebPageSchema(
        "Our Team - BHODEV Cameroon",
        "Meet the dedicated team at BHODEV Cameroon driving health, education, and protection programs across Cameroon.",
        "https://bhodevcameroon.org/our-team"
      ),
    },
  });

  useEffect(() => {
    document.title = "Our Team | BHODEV Cameroon - Bogi Health Organization for Development";

    async function fetchStaff() {
      try {
        const { data, error: supaError } = await supabase
          .from("staff_members")
          .select("*")
          .eq("status", "Active")
          .order("role", { ascending: true });

        if (supaError) {
          console.error("Error fetching staff:", supaError);
          setError("Unable to load team data. Please try again later.");
        } else {
          setStaff(data || []);
          setError(null);
        }
      } catch (err) {
        console.error("Network error fetching staff:", err);
        setError("Unable to load team data. Please check your connection and try again.");
      }
      setLoading(false);
    }

    fetchStaff();
  }, []);

  const ceo = staff.find((s) => s.role?.toLowerCase().includes("chief executive") || s.role?.toLowerCase().includes("ceo"));
  const executiveMembers = staff.filter((s) => s.id !== ceo?.id);

  const activeFilters = ["All", ...Array.from(new Set(staff.map((s) => s.department)))];
  const [activeFilter, setActiveFilter] = useState("All");

  const filteredMembers = activeFilter === "All" ? executiveMembers : executiveMembers.filter((s) => s.department === activeFilter);

  return (
    <div className="min-h-screen bg-white font-inter">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
        <img
          src="https://firebasestorage.googleapis.com/v0/b/bhodevcameroon.appspot.com/o/company-assets%2FIMG_6332%20-%20BOGI%20HEALTH%20ORGANIZATION%20FOR%20DEVELOPMENT%20-%20BHODEV.jpeg?alt=media&token=e798946f-695a-4d45-b6cc-96cfd3864a66"
          alt="BHODEV Team in Cameroon"
          title="BHODEV Team in Cameroon"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
        <div className="relative z-10 text-center text-white px-4 w-full max-w-4xl mx-auto">
          <nav className="flex items-center justify-center gap-2 text-xs text-white/60 mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/about" className="hover:text-white transition-colors">About Us</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white/90">Our Team</span>
          </nav>
          <span className="inline-block bg-primary/20 border border-primary/40 text-primary-light text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-5">
            Team Members
          </span>
          <h1 className="text-4xl md:text-6xl font-bold mb-5 leading-tight">
            The People Behind<br />
            <span className="text-primary">BHODEV</span>
          </h1>
          <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
            We have a well equipped and diverse team made up of Social Entrepreneurs, Medical Doctors, Experienced Nurses, Educational Experts, Managers and Psychologists.
          </p>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-stone-900 py-6">
        <div className="max-w-6xl mx-auto px-4 md:px-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div>
            <p className="text-3xl font-bold text-primary">{staff.length || "20"}+</p>
            <p className="text-white/60 text-sm mt-1">Team Members</p>
          </div>
          <div>
            <p className="text-3xl font-bold text-primary">4</p>
            <p className="text-white/60 text-sm mt-1">Core Departments</p>
          </div>
          <div>
            <p className="text-3xl font-bold text-primary">10+</p>
            <p className="text-white/60 text-sm mt-1">Years of Service</p>
          </div>
          <div>
            <p className="text-3xl font-bold text-primary">50K+</p>
            <p className="text-white/60 text-sm mt-1">Lives Impacted</p>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-secondary text-primary text-xs font-semibold tracking-widest uppercase px-3 py-1 rounded-full mb-4">
                Who We Are
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-5 leading-tight">
                The Chief Executive and the Executive Management Team
              </h2>
              <p className="text-stone-600 leading-relaxed mb-4">
                The executive team of Bolgi Health Development Cameroon and the Chief Executive Officer are responsible for running BHODEV Cameroon&apos;s daily affairs.
              </p>
              <p className="text-stone-600 leading-relaxed mb-6">
                Our multidisciplinary team brings together expertise from healthcare, education, social entrepreneurship, and community development — united by a shared commitment to improving lives across Cameroon.
              </p>
              <div className="flex flex-wrap gap-3">
                {["Social Entrepreneurs", "Medical Doctors", "Nurses", "Educators", "Managers"].map((tag) => (
                  <span key={tag} className="bg-secondary text-primary text-xs font-medium px-3 py-1.5 rounded-full border border-primary/20">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://firebasestorage.googleapis.com/v0/b/bhodevcameroon.appspot.com/o/project-assets%2Fsensitization-on-water-hygiene-and-sanitation-mile-16%2FIMG-20230504-WA0066.jpg?alt=media&token=c1894e5b-f286-423f-ac50-4c54636d8865"
                alt="BHODEV Executive Team Meeting in Cameroon"
                title="BHODEV Executive Team Meeting in Cameroon"
                className="w-full h-[320px] md:h-[380px] object-cover object-top rounded-2xl"
              />
              <div className="absolute -bottom-4 -left-4 bg-primary text-white px-5 py-3 rounded-xl shadow-lg">
                <p className="text-2xl font-bold">10+</p>
                <p className="text-xs text-white/80">Years of Impact</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CEO Section */}
      {ceo && (
        <section className="py-16 md:py-20 bg-stone-50">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <span className="inline-block bg-secondary text-primary text-xs font-semibold tracking-widest uppercase px-3 py-1 rounded-full mb-3">
                Leadership
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-stone-900">Chief Executive Officer</h2>
            </div>

            <div className="max-w-3xl mx-auto">
              <div className="bg-white rounded-2xl overflow-hidden border border-stone-100 flex flex-col md:flex-row" itemScope itemType="https://schema.org/Person">
                <div className="md:w-64 flex-shrink-0">
                  <img
                    src={ceo.image_url || "https://readdy.ai/api/search-image?query=professional%20african%20male%20executive%20portrait%2C%20formal%20attire%2C%20confident%20smile%2C%20clean%20studio%20background%2C%20high%20quality%20headshot%20photography&width=400&height=400&seq=ceo-hans-001&orientation=squarish"}
                    alt={ceo.name}
                    title={ceo.name}
                    className="w-full h-64 md:h-full object-cover object-top"
                    itemProp="image"
                  />
                </div>
                <div className="p-8 flex flex-col justify-center">
                  <span className="inline-block bg-primary text-white text-xs font-semibold tracking-wider uppercase px-3 py-1 rounded-full mb-4 w-fit">
                    CEO
                  </span>
                  <h3 className="text-2xl font-bold text-stone-900 mb-1" itemProp="name">{ceo.name}</h3>
                  <p className="text-primary font-semibold text-sm mb-4" itemProp="jobTitle">{ceo.role}</p>
                  <p className="text-stone-600 text-sm leading-relaxed mb-6" itemProp="description">
                    Founder and CEO of BHODEV Cameroon, Hans Asa Tanga has dedicated over a decade to improving health, education, and economic security for vulnerable populations in Cameroon. A social entrepreneur and humanitarian leader, he drives the organization&apos;s strategic vision and daily operations.
                  </p>
                  <div className="flex gap-3">
                    <a href="/about#founder" className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-user-line"></i> Full Profile
                    </a>
                    <a href="/contact" className="inline-flex items-center gap-2 border border-stone-200 text-stone-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-mail-line"></i> Contact
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Executive Members */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="inline-block bg-secondary text-primary text-xs font-semibold tracking-widest uppercase px-3 py-1 rounded-full mb-3">
              The Team
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-3">Executive Members</h2>
            <p className="text-stone-500 max-w-xl mx-auto text-sm">
              Our dedicated executive team drives BHODEV&apos;s mission across health, education, protection, and economic security.
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {activeFilters.map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeFilter === filter
                    ? "bg-primary text-white"
                    : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                }`}
              >
                {filter}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-16">
              <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-stone-500 text-sm">Loading team members...</p>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <div className="w-14 h-14 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-wifi-off-line text-2xl text-stone-400"></i>
              </div>
              <p className="text-stone-600 font-medium mb-2">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i> Retry
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {filteredMembers.map((member) => (
                <div
                  key={member.id}
                  className="group bg-white border border-stone-100 rounded-2xl overflow-hidden hover:border-primary/30 transition-all duration-300"
                  itemScope
                  itemType="https://schema.org/Person"
                >
                  <div className="relative overflow-hidden h-64">
                    <img
                      src={member.image_url || "https://readdy.ai/api/search-image?query=professional%20african%20person%20portrait%2C%20formal%20attire%2C%20warm%20smile%2C%20clean%20light%20background%2C%20headshot%20photography&width=300&height=300&seq=team-" + member.id.slice(0, 6) + "&orientation=squarish"}
                      alt={member.name}
                      title={member.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      itemProp="image"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-stone-900 text-sm leading-snug mb-1" itemProp="name">{member.name}</h3>
                    <p className="text-primary text-xs font-medium mb-2" itemProp="jobTitle">{member.role}</p>
                    <p className="text-stone-400 text-xs">{member.department}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Departments */}
      <section className="py-16 md:py-20 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="inline-block bg-secondary text-primary text-xs font-semibold tracking-widest uppercase px-3 py-1 rounded-full mb-3">
              Structure
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-stone-900 mb-3">Our Departments</h2>
            <p className="text-stone-500 max-w-xl mx-auto text-sm">
              Each department is led by experienced professionals committed to delivering impact in their respective fields.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {departments.map((dept) => {
              const count = staff.filter((s) => s.department === dept.name).length;
              return (
                <div key={dept.name} className={`rounded-2xl border p-6 ${dept.color}`}>
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-white mb-4">
                    <i className={`${dept.icon} text-xl ${dept.iconColor}`}></i>
                  </div>
                  <h3 className="font-bold text-lg mb-1">{dept.name}</h3>
                  <p className="text-xs opacity-80 leading-relaxed mb-3">{dept.description}</p>
                  <span className="text-xs font-semibold opacity-70">{count || "4+"} members</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Join the Team CTA */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="relative rounded-3xl overflow-hidden">
            <img
              src="https://firebasestorage.googleapis.com/v0/b/bhodevcameroon.appspot.com/o/project-assets%2Fsensitization-on-water-hygiene-and-sanitation-mile-16%2FIMG-20230504-WA0066.jpg?alt=media&token=c1894e5b-f286-423f-ac50-4c54636d8865"
              alt="Join BHODEV Team - Volunteer and Intern Opportunities in Cameroon"
              title="Join BHODEV Team - Volunteer and Intern Opportunities in Cameroon"
              className="w-full h-[300px] md:h-[360px] object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-stone-900/90 via-stone-900/70 to-transparent"></div>
            <div className="absolute inset-0 flex items-center px-8 md:px-16">
              <div className="max-w-lg">
                <span className="inline-block bg-primary/20 border border-primary/40 text-primary text-xs font-semibold tracking-widest uppercase px-3 py-1 rounded-full mb-4">
                  Join Us
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
                  Become Part of Our Team
                </h2>
                <p className="text-white/70 text-sm leading-relaxed mb-6">
                  Are you passionate about health, education, and community development? We&apos;re always looking for dedicated individuals to join our mission.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/get-involved"
                    className="bg-primary text-white px-6 py-3 rounded-full font-semibold text-sm hover:bg-primary/90 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Get Involved
                  </Link>
                  <Link
                    to="/contact"
                    className="bg-white/10 border border-white/30 text-white px-6 py-3 rounded-full font-semibold text-sm hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Contact Us
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}